import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Link, Typography } from '@mui/material';
import { useTheme } from '@parkdna/ui/theme';

import { useSidebarStore } from '@/stores/sidebar';

/**
 * Props for the FAQLink component
 */
interface FAQLinkProps {
  /**
   * The URL to the FAQ page. Must be a valid HTTP or HTTPS URL.
   * If not provided or invalid, the component will not render.
   * @example "https://example.com/faq"
   */
  faqUrl?: string;
}

/**
 * A link component that displays an FAQ help icon at the bottom of the sidebar.
 *
 * Features:
 * - Displays a help icon (question mark) that links to an FAQ page
 * - Opens the FAQ in a new tab with proper security attributes (noopener noreferrer)
 * - Adapts to sidebar collapsed state (shows only icon when collapsed)
 * - Includes URL validation to prevent XSS attacks
 *
 * Security:
 * - Only renders for valid HTTP/HTTPS URLs
 * - Blocks javascript:, data:, and relative URLs
 *
 * @example
 * ```tsx
 * // With FAQ URL
 * <FAQLink faqUrl="https://example.com/faq" />
 *
 * // Without URL (won't render)
 * <FAQLink />
 * ```
 */
export const FAQLink = ({ faqUrl }: FAQLinkProps) => {
  const [theme] = useTheme();
  const isCollapsed = useSidebarStore((state) => state.isCollapsed);

  // Only render if faqUrl is provided and is a valid HTTP(S) URL
  if (!faqUrl || !faqUrl.startsWith('http')) {
    return null;
  }

  return (
    <Box
      sx={{
        padding: isCollapsed ? '0 0.5rem' : '0 1rem',
        marginBottom: 2,
      }}
    >
      <Link
        href={faqUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-no-external-icon={isCollapsed || undefined}
        underline="none"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          padding: isCollapsed ? '0.5rem' : '0.75rem 1rem',
          borderRadius: `${theme.shape.borderRadius}px`,
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          transition: theme.transitions.create(['background-color', 'color'], {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <HelpOutlineIcon
          sx={{
            fontSize: 20,
            color: theme.palette.text.secondary,
          }}
        />
        {!isCollapsed && (
          <Typography variant="body2" sx={{ flex: 1 }}>
            FAQ
          </Typography>
        )}
      </Link>
    </Box>
  );
};
